import React, { createContext, useContext, useMemo, useState } from 'react';

import type { OrganizationDetail } from '@rtkwlf/eloc';
import { StringParam, useQueryParams } from 'use-query-params';

import { STORAGE_KEYS } from 'ae-constants/constants';

interface SelectedOrganizationValue {
  selectedOrganization?: OrganizationDetail;
  setSelectedOrganization: React.Dispatch<
    React.SetStateAction<OrganizationDetail | undefined>
  >;
}

const SelectedOrganizationContext = createContext<
  SelectedOrganizationValue | undefined
>(undefined);

const ERROR_MESSAGE =
  'useSelectedOrganizationContext hook must be used within an SelectedOrganizationContext.Provider; ensure your node is wrapped in one.';

export const useSelectedOrganizationContext = (): SelectedOrganizationValue => {
  const ctx = useContext(SelectedOrganizationContext);
  if (ctx === undefined) {
    throw new Error(ERROR_MESSAGE);
  } else {
    return ctx;
  }
};

export const SelectedOrganizationProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [queryParams] = useQueryParams({
    id: StringParam,
    org: StringParam,
  });

  const queryParamsOrgId = useMemo(
    () => queryParams.org || queryParams.id,
    [queryParams.id, queryParams.org],
  );

  const defaultSelected = useMemo(() => {
    if (queryParamsOrgId) {
      return {
        customerID: '',
        id: queryParamsOrgId,
        name: '',
        rendallURL: '',
      };
    }

    const selectedOrgFromStorage = window.localStorage.getItem(
      STORAGE_KEYS.AWN_ORGANIZATION,
    );

    if (selectedOrgFromStorage) {
      return JSON.parse(selectedOrgFromStorage);
    }

    return undefined;
  }, [queryParamsOrgId]);

  const [selectedOrganization, setSelectedOrganization] = useState<
    OrganizationDetail | undefined
  >(defaultSelected);

  const value = useMemo(() => {
    return { selectedOrganization, setSelectedOrganization };
  }, [selectedOrganization, setSelectedOrganization]);

  return (
    <SelectedOrganizationContext.Provider value={value}>
      {children}
    </SelectedOrganizationContext.Provider>
  );
};
